import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";
import {
    EarlyBirdOfferDto,
    EarlyBirdOfferItemDto,
} from "app/shared/model/EarlyBirdOfferDto";

function buildUrl(
    templateKey: keyof typeof apiConfig,
    divisionId: number,
    year: number
): string {
    const template = (apiConfig as Record<string, string>)[templateKey];

    if (!template) {
        throw new Error(`Missing apiConfig key: ${String(templateKey)}`);
    }

    return template
        .replace("{divisionId}", encodeURIComponent(String(divisionId)))
        .replace("{year}", encodeURIComponent(String(year)));
}

function buildDivisionUrl(
    templateKey: keyof typeof apiConfig,
    divisionId: number
): string {
    const template = (apiConfig as Record<string, string>)[templateKey];

    if (!template) {
        throw new Error(`Missing apiConfig key: ${String(templateKey)}`);
    }

    return template.replace(
        "{divisionId}",
        encodeURIComponent(String(divisionId))
    );
}

function mapOffer(dto: any): EarlyBirdOfferDto {
    if (dto == null) {
        return { rules: [] };
    }

    const rules = Array.isArray(dto.rules) ? dto.rules : [];

    return {
        rules: rules.map((r: any) => ({
            name: r?.name ?? "",
            earlyBirdEndDate: r?.earlyBirdEndDate ?? "",
            include: Array.isArray(r?.include) ? r.include : [],
            exclude: Array.isArray(r?.exclude) ? r.exclude : [],
        })),
    };
}

function mapOfferItem(dto: any): EarlyBirdOfferItemDto {
    return {
        year: Number(dto?.year ?? 0),
        lastModifiedBy: String(dto?.lastModifiedBy ?? ""),
        updateTimestamp: String(dto?.updateTimestamp ?? ""),
        active: Boolean(dto?.active ?? true),
    };
}

async function getResponseText(response: Response | undefined): Promise<string> {
    return (await response?.text().catch(() => "")) ?? "";
}

export async function getEarlyBirdOffers(
    divisionId: number
): Promise<EarlyBirdOfferItemDto[]> {
    const localApi = await getLocalApi();
    const url = buildDivisionUrl("early_bird_offer_get_all", divisionId);

    const response = await localApi.call(url, "GET", undefined);

    if (response?.status === 404) {
        return [];
    }

    if (!response?.ok) {
        const text = await getResponseText(response);
        throw new Error(
            `Failed to fetch early bird offers (HTTP ${response?.status}) — ${
                text || response?.statusText
            }`.trim()
        );
    }

    const dto = await response.json().catch(() => []);
    return Array.isArray(dto) ? dto.map(mapOfferItem) : [];
}

export async function getEarlyBirdOffer(
    divisionId: number,
    year: number
): Promise<EarlyBirdOfferDto> {
    const localApi = await getLocalApi();
    const url = buildUrl("early_bird_offer_get", divisionId, year);

    const response = await localApi.call(url, "GET", undefined);

    if (response?.status === 404) {
        return { rules: [] };
    }

    if (!response?.ok) {
        const text = await getResponseText(response);
        throw new Error(
            `Failed to fetch early bird offer (HTTP ${response?.status}) — ${
                text || response?.statusText
            }`.trim()
        );
    }

    const dto = await response.json().catch(() => null);
    return mapOffer(dto);
}

export async function upsertEarlyBirdOffer(
    divisionId: number,
    year: number,
    dto: EarlyBirdOfferDto
): Promise<void> {
    const localApi = await getLocalApi();
    const url = buildUrl("early_bird_offer_upsert", divisionId, year);
    const response = await localApi.call(url, "PUT", JSON.stringify(dto));

    if (!response?.ok) {
        const text = await getResponseText(response);
        throw new Error(
            `Failed to save early bird offer (HTTP ${response?.status}) — ${
                text || response?.statusText
            }`.trim()
        );
    }
}

export async function deleteEarlyBirdOffer(
    divisionId: number,
    year: number
): Promise<void> {
    const localApi = await getLocalApi();
    const url = buildUrl("early_bird_offer_delete", divisionId, year);
    const response = await localApi.call(url, "DELETE", undefined);

    if (!response?.ok) {
        const text = await getResponseText(response);
        throw new Error(
            `Failed to delete early bird offer (HTTP ${response?.status}) — ${
                text || response?.statusText
            }`.trim()
        );
    }
}